import { Box, Typography, Grid, Container, TextField } from "@mui/material";
import { ClientModel, AddressModel } from "../../Utils/Models";
import { Vocabulary } from "../../Utils/Vocabulary";
import ScanIdButton from "../ScanIdButton";

interface ClientFormStepProps {
  clientData: ClientModel;
  agreeTerms: boolean;
  agreePrivacy: boolean;
  onClientDataChange: (field: keyof ClientModel, value: any) => void;
  onAgreeTermsChange: (value: boolean) => void;
  onAgreePrivacyChange: (value: boolean) => void;
  onScanComplete: (data: any) => void;
  showScanButton?: boolean;
  showCNP?: boolean;
}

export default function ClientFormStep({
  clientData,
  agreeTerms,
  agreePrivacy,
  onClientDataChange,
  onAgreeTermsChange,
  onAgreePrivacyChange,
  onScanComplete,
  showScanButton = true,
  showCNP = true,
}: ClientFormStepProps) {
  const handleAddressChange = (field: keyof AddressModel, value: string) => {
    const updatedAddress = {
      ...clientData.address,
      [field]: value,
    };
    onClientDataChange("address", updatedAddress);
  };

  return (
    <Container maxWidth="md">
      {showScanButton && (
        <Box textAlign="center" mb={4}>
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <ScanIdButton
              onScanComplete={onScanComplete}
              variant="contained"
              size="large"
              showFullText={true}
              sx={{
                bgcolor: "#4CB6AC",
                color: "white",
                borderRadius: "1rem",
                px: 3,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "0 10px 25px rgba(76, 182, 172, 0.25)",
                border: "2px solid #4CB6AC",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "#14b8a6",
                  boxShadow: "0 15px 35px rgba(76, 182, 172, 0.35)",
                  transform: "scale(1.05)",
                },
              }}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          p: 4,
          borderRadius: "24px",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
          border: "2px solid #e2e8f0",
          backgroundColor: "white",
          transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          "&:hover": {
            boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {/* PERSONAL INFORMATION SECTION */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                background: "linear-gradient(135deg, #4CB6AC 0%, #14b8a6 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="white"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </Box>
            <Typography
              sx={{ fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}
            >
              {Vocabulary.personalInfo}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "#64748b",
              mb: 2.5,
              lineHeight: 1.6,
            }}
          >
            Informațiile vor rămâne confidențiale și securizate.
          </Typography>

          <Box
            sx={{
              borderRadius: "16px",
              p: 2.5,
            }}
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={Vocabulary.firstName}
                  value={clientData.firstName || ""}
                  onChange={(e) =>
                    onClientDataChange(
                      "firstName",
                      e.target.value.toUpperCase()
                    )
                  }
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={Vocabulary.lastName}
                  value={clientData.lastName || ""}
                  onChange={(e) =>
                    onClientDataChange("lastName", e.target.value.toUpperCase())
                  }
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={Vocabulary.email}
                  type="email"
                  value={clientData.email || ""}
                  onChange={(e) => onClientDataChange("email", e.target.value)}
                  required
                  variant="outlined"
                  error={
                    clientData.email
                      ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)
                      : false
                  }
                  helperText={
                    clientData.email &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)
                      ? Vocabulary.invalidEmail
                      : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                      "&.Mui-error fieldset": {
                        borderColor: "#ef4444",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={Vocabulary.phone}
                  value={clientData.phone || ""}
                  onChange={(e) => onClientDataChange("phone", e.target.value)}
                  required
                  variant="outlined"
                  error={
                    clientData.phone
                      ? !/^(\+4|4|0)\d{8,9}$/.test(
                          clientData.phone.replace(/\s/g, "")
                        )
                      : false
                  }
                  helperText={
                    clientData.phone &&
                    !/^(\+4|4|0)\d{8,9}$/.test(
                      clientData.phone.replace(/\s/g, "")
                    )
                      ? Vocabulary.phoneValidation
                      : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                      "&.Mui-error fieldset": {
                        borderColor: "#ef4444",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
              {showCNP && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={Vocabulary.CNP}
                    type="number"
                    value={clientData.CNP || ""}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 13);
                      onClientDataChange("CNP", value);
                    }}
                    required
                    variant="outlined"
                    error={clientData.CNP ? clientData.CNP.length !== 13 : false}
                    helperText={
                      clientData.CNP && clientData.CNP.length !== 13
                        ? "CNP-ul trebuie să aibă 13 cifre"
                        : Vocabulary.cnpHelperText
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        fontSize: "0.95rem",
                        transition: "all 0.3s ease",
                        backgroundColor: "#fafafa",
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                          borderWidth: "1.5px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#4CB6AC",
                          borderWidth: "2px",
                        },
                        "&.Mui-error fieldset": {
                          borderColor: "#ef4444",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.9rem",
                        fontWeight: 500,
                        color: "#64748b",
                        "&.Mui-focused": {
                          color: "#4CB6AC",
                        },
                      },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>

        {/* ADDRESS INFORMATION SECTION */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                background: "linear-gradient(135deg, #4CB6AC 0%, #14b8a6 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="white"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </Box>
            <Typography
              sx={{ fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}
            >
              {Vocabulary.addressInfo}
            </Typography>
          </Box>

          <Box sx={{  borderRadius: "16px", p: 2.5 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={Vocabulary.country}
                  value={clientData.address?.country || ""}
                  onChange={(e) =>
                    handleAddressChange("country", e.target.value)
                  }
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={Vocabulary.county}
                  value={clientData.address?.county || ""}
                  onChange={(e) =>
                    handleAddressChange("county", e.target.value)
                  }
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={Vocabulary.city}
                  value={clientData.address?.city || ""}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      transition: "all 0.3s ease",
                      backgroundColor: "#fafafa",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CB6AC",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "#64748b",
                      "&.Mui-focused": {
                        color: "#4CB6AC",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* TERMS & CONDITIONS SECTION */}
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box
              onClick={() => onAgreeTermsChange(!agreeTerms)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "16px 20px",
                borderRadius: "12px",
                border: "2px solid #e2e8f0",
                background: agreeTerms
                  ? "linear-gradient(to right, rgba(76, 182, 172, 0.1), rgba(20, 184, 166, 0.1))"
                  : "rgba(255, 255, 255, 0.6)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: agreeTerms ? "#4CB6AC" : "#cbd5e1",
                  background: agreeTerms
                    ? "linear-gradient(to right, rgba(76, 182, 172, 0.15), rgba(20, 184, 166, 0.15))"
                    : "rgba(224, 225, 225, 0.37)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 24,
                  borderRadius: "12px",
                  background: agreeTerms
                    ? "linear-gradient(to right, #4CB6AC, #14b8a6)"
                    : "#e2e8f0",
                  position: "relative",
                  transition: "all 0.3s ease",
                  boxShadow: agreeTerms
                    ? "0 4px 8px rgba(76, 182, 172, 0.3)"
                    : "0 2px 4px rgba(0, 0, 0, 0.1)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "2px",
                    left: agreeTerms ? "26px" : "2px",
                    width: 20,
                    height: 20,
                    borderRadius: "10px",
                    background: "white",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "#334155",
                  lineHeight: 1.5,
                  flex: 1,
                  userSelect: "none",
                }}
              >
                {Vocabulary.agreeTermsAndConditions}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              onClick={() => onAgreePrivacyChange(!agreePrivacy)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "16px 20px",
                borderRadius: "12px",
                border: "2px solid #e2e8f0",
                background: agreePrivacy
                  ? "linear-gradient(to right, rgba(76, 182, 172, 0.1), rgba(20, 184, 166, 0.1))"
                  : "rgba(255, 255, 255, 0.6)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: agreePrivacy ? "#4CB6AC" : "#cbd5e1",
                  background: agreePrivacy
                    ? "linear-gradient(to right, rgba(76, 182, 172, 0.15), rgba(20, 184, 166, 0.15))"
                    : "rgba(224, 225, 225, 0.37)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 24,
                  borderRadius: "12px",
                  background: agreePrivacy
                    ? "linear-gradient(to right, #4CB6AC, #14b8a6)"
                    : "#e2e8f0",
                  position: "relative",
                  transition: "all 0.3s ease",
                  boxShadow: agreePrivacy
                    ? "0 4px 8px rgba(76, 182, 172, 0.3)"
                    : "0 2px 4px rgba(0, 0, 0, 0.1)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "2px",
                    left: agreePrivacy ? "26px" : "2px",
                    width: 20,
                    height: 20,
                    borderRadius: "10px",
                    background: "white",
                    transition: "all 0.3s ease",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "#334155",
                  lineHeight: 1.5,
                  flex: 1,
                  userSelect: "none",
                }}
              >
                {Vocabulary.agreePrivacyPolicy}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
